import React, { useEffect, useRef, useState } from "react"
import { ChevronLeftIcon, PauseIcon, PlayIcon } from "@heroicons/react/24/solid"
import Button from "aldoo-ra/Button"
import { twMerge } from "tailwind-merge"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import useSiteLanguage from "../hooks/useSiteLanguage"
import { GetMusicAlbumPreview } from "../api"
import { pausePlayer } from "../player"

/**
 * "Preview" button for music albums: loads product {music_album_preview} and
 * opens a dark track list; each track plays its 30s preview (HTML5 audio).
 */
export default function PlaylistPreviewButton({ product, className = "" }) {
  const t = useT()
  const language = useSiteLanguage()
  const [open, setOpen] = useState(false)
  const [current, setCurrent] = useState(-1)
  // what the row shows has to be what the sound is doing, including a pause
  // that came from somewhere else (the headphone button, another tab)
  const [paused, setPaused] = useState(true)
  const audioRef = useRef(null)

  const { result } = useAsync(() => GetMusicAlbumPreview(product.pid, language), [product?.pid, language], { enabled: !!product?.pid })
  const tracks = result?.tracks || []

  const stop = () => {
    audioRef.current?.pause()
    audioRef.current = null
    setCurrent(-1)
    setPaused(true)
  }

  useEffect(() => {
    if (!open) stop()
    return stop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const play = (index) => {
    // the same row again is a pause, and a second press starts it over from
    // where it stopped
    if (current === index && audioRef.current) {
      if (audioRef.current.paused) return audioRef.current.play().catch(() => stop())
      return audioRef.current.pause()
    }
    audioRef.current?.pause()
    // nothing else should be sounding while a snippet plays
    pausePlayer()
    const audio = new Audio(tracks[index].audio)
    audio.onended = stop
    audio.onplay = () => setPaused(false)
    audio.onpause = () => setPaused(true)
    audioRef.current = audio
    setCurrent(index)
    setPaused(false)
    audio.play().catch(() => stop())
  }

  return (
    <>
      <Button variant="primary_dark" className={`min-w-[150px] ${className}`} onClick={() => setOpen(true)}>
        {t`Preview`}
      </Button>
      {open && (
        <div className="fixed inset-0 z-[500] bg-black/70 flex items-center justify-center" onClick={() => setOpen(false)}>
          <div className="relative w-screen h-[100dvh] sm:w-[964px] sm:h-[544px] bg-dark shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 left-4 text-white p-1 rounded hover:bg-white/10" aria-label={t`Close`}>
              <ChevronLeftIcon className="w-8 h-8" />
            </button>
            <h2 className="h-[60px] mt-2 mb-0 flex items-center justify-center text-white text-[25px] font-bold px-14 text-center truncate">{product.name}</h2>
            {/* the list scrolls down only: a row that grew on hover pushed
                the list sideways and put a scrollbar under it */}
            <ul className="m-0 p-0 list-none h-[calc(100dvh-74px)] sm:h-[470px] overflow-y-auto overflow-x-hidden scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent text-white">
              {tracks.map((track, index) => {
                const playing = current === index
                return (
                  <li
                    key={index}
                    onClick={() => play(index)}
                    className={twMerge("flex items-center justify-between gap-4 px-5 py-2 cursor-pointer transition-colors hover:bg-white/10", playing && "text-primary bg-primary/10")}
                  >
                    <div className="flex items-center gap-4">
                      <span className="w-6 text-right text-[14px] text-gray_2">
                        {!playing && index + 1}
                        {playing && !paused && <PauseIcon className="w-5 h-5 text-primary inline" />}
                        {playing && paused && <PlayIcon className="w-5 h-5 text-primary inline" />}
                      </span>
                      <div className="flex flex-col">
                        <span className="text-[15px]">{track.name}</span>
                        <span className="text-[12px] text-gray_2">{track.duration}</span>
                      </div>
                    </div>
                    <span
                      className={twMerge(
                        "inline-flex items-center justify-center w-[100px] h-[30px] rounded-full border text-[13px]",
                        playing ? "border-primary text-primary" : "border-white/60"
                      )}
                    >
                      {playing ? (paused ? t`Paused` : t`Playing`) : t`Preview`}
                    </span>
                  </li>
                )
              })}
              {tracks.length === 0 && result && <li className="px-5 py-6 text-gray_2">{t`No preview available`}</li>}
              <li className="h-24" />
            </ul>
          </div>
        </div>
      )}
    </>
  )
}
